import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaFilter,
  FaEye,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaFileInvoiceDollar
} from 'react-icons/fa';
import CompactActionBar from '../components/CompactActionBar';
import SharedFilter from '../components/SharedFilter';
import { expenseService, type Expense, type ExpenseFilters, type ExpenseHead } from '../services/expenseService';
import { projectService } from '../services/projectService';
import ConfirmDialog from '../components/ConfirmDialog';
import { PermissionGuard } from '../components/PermissionGuard';
import { usePageTitle } from '../hooks/usePageTitle';
import { useHiddenMode } from '../contexts/HiddenModeContext';
import '../css/standard-components.css';
import '../css/hiddenIncome.css';  // For unauthorized access styles

interface Project {
  id: number;
  name: string;
}

const ExpensesPage: React.FC = () => {
  usePageTitle();
  const { isHiddenModeActive } = useHiddenMode();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [expenseHeads, setExpenseHeads] = useState<ExpenseHead[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState<Expense | null>(null);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(100);
  
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filterProject, setFilterProject] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterExpenseHead, setFilterExpenseHead] = useState("");
  const [filterNarration, setFilterNarration] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    project: "",
    dateFrom: "",
    dateTo: "",
    expenseHead: "",
    narration: "",
  });
  
  // Summary data
  const [summary, setSummary] = useState<{
    totalExpenses: number;
    totalAmount: number;
    expensesByHead: { name: string; count: number; amount: number }[];
    expensesByProject: { name: string; count: number; amount: number }[];
  }>({
    totalExpenses: 0,
    totalAmount: 0,
    expensesByHead: [],
    expensesByProject: []
  });

  const handleApplyFilters = () => {
    setAppliedFilters({
      project: filterProject,
      dateFrom: filterDateFrom,
      dateTo: filterDateTo,
      expenseHead: filterExpenseHead,
      narration: filterNarration,
    });
    setCurrentPage(1);
    setShowFilters(false);
  };

  const clearAllFilters = () => {
    setFilterProject("");
    setFilterDateFrom("");
    setFilterDateTo("");
    setFilterExpenseHead("");
    setFilterNarration("");
    setAppliedFilters({
      project: "",
      dateFrom: "",
      dateTo: "",
      expenseHead: "",
      narration: "",
    });
    setCurrentPage(1);
  };

  // Function definitions - moved before early return to avoid hoisting issues
  const loadExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const filters: ExpenseFilters = {
        search: appliedFilters.narration || '',
        projectId: appliedFilters.project ? parseInt(appliedFilters.project) : undefined,
        expenseHeadId: appliedFilters.expenseHead || undefined,
        startDate: appliedFilters.dateFrom || '',
        endDate: appliedFilters.dateTo || ''
      };
      
      const response = await expenseService.getExpenses({
        ...filters,
        page: currentPage,
        limit: limit
      });
      setExpenses(response.data);
      setTotal(response.pagination.totalRecords);
      setTotalPages(response.pagination.totalPages);
      
      // Calculate summary data
      calculateSummary(response.data);
    } catch (error) {
      console.error('Error loading expenses:', error);
    } finally {
      setLoading(false);
    }
  }, [appliedFilters, currentPage, limit]);

  const calculateSummary = useCallback((expenseData: Expense[]) => {
    const totalExpenses = expenseData.length;
    const totalAmount = expenseData.reduce((sum, expense) => sum + expense.amount, 0);
    
    // Group by expense head
    const expensesByHead = expenseData.reduce((acc, expense) => {
      const headName = expense.expenseHead.name;
      if (!acc[headName]) {
        acc[headName] = { count: 0, amount: 0 };
      }
      acc[headName].count += 1;
      acc[headName].amount += expense.amount;
      return acc;
    }, {} as Record<string, { count: number; amount: number }>);
    
    // Group by project
    const expensesByProject = expenseData.reduce((acc, expense) => {
      const projectName = expense.project.name;
      if (!acc[projectName]) {
        acc[projectName] = { count: 0, amount: 0 };
      }
      acc[projectName].count += 1;
      acc[projectName].amount += expense.amount;
      return acc;
    }, {} as Record<string, { count: number; amount: number }>);
    
    setSummary({
      totalExpenses,
      totalAmount,
      expensesByHead: Object.entries(expensesByHead).map(([name, data]) => ({
        name,
        ...data
      })).sort((a, b) => b.amount - a.amount),
      expensesByProject: Object.entries(expensesByProject).map(([name, data]) => ({
        name,
        ...data
      })).sort((a, b) => b.amount - a.amount)
    });
  }, []);

  const loadProjects = useCallback(async () => {
    try {
      const response = await projectService.getProjects();
      const activeProjects = response.filter((p: any) => p.isActive);
      setProjects(activeProjects);
      
      // Auto-select first project
      if (activeProjects.length > 0) {
        const firstProjectId = activeProjects[0].id.toString();
        setFilterProject(firstProjectId);
        setAppliedFilters(prev => ({ ...prev, project: firstProjectId }));
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  }, []);

  const loadExpenseHeads = useCallback(async () => {
    try {
      const data = await expenseService.getExpenseHeads();
      setExpenseHeads(data);
    } catch (error) {
      console.error('Error loading expense heads:', error);
    }
  }, []);

  // Load projects and expense heads only once on mount
  useEffect(() => {
    loadProjects();
    loadExpenseHeads();
  }, []);

  // Load expenses when filters, page, or limit change
  useEffect(() => {
    loadExpenses();
  }, [currentPage, appliedFilters, limit]);

  // Refresh expense heads when page becomes visible (e.g., returning from add expense page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadExpenseHeads();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Redirect if not in hidden mode
  if (!isHiddenModeActive) {
    return (
      <div className="page-container">
        <div className="unauthorized-access">
          <div className="unauthorized-content">
            <h2>🔒 Access Restricted</h2>
            <p>Expense Management is only available when Hidden Mode is active.</p>
            <p>Please activate Hidden Mode to access these features.</p>
            <div style={{marginTop: '20px', fontSize: '14px', color: '#666'}}>
              <p><strong>How to activate:</strong></p>
              <p>Press <code>Alt + Ctrl + Shift + 6</code> to activate Hidden Mode</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleDelete = (expense: Expense) => {
    setExpenseToDelete(expense);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (expenseToDelete) {
      try {
        await expenseService.deleteExpense(expenseToDelete.id);
        loadExpenses();
        setShowDeleteDialog(false);
        setExpenseToDelete(null);
      } catch (error) {
        console.error('Error deleting expense:', error);
      }
    }
  };

  const handleEdit = (expense: Expense) => {
    navigate(`/ledger/expenses/edit?id=${expense.id}`);
  };

  return (
    <div className="page-container">
      {/* Action Bar */}
      <CompactActionBar>
        <div style={{ display: "flex", gap: "12px", alignItems: "center", flex: 1 }}>
          <button
            className={`filter-toggle-btn ${showFilters ? "active" : ""}`}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> Filters
          </button>

          <div className="cab-filter-group">
            <span>Project:</span>
            <select
              className="cab-select"
              value={filterProject}
              onChange={(e) => {
                setFilterProject(e.target.value);
                setAppliedFilters((prev) => ({ ...prev, project: e.target.value }));
                setCurrentPage(1);
              }}
            >
              <option value="">All Projects</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
          </div>

          <div className="cab-filter-group">
            <span>Show:</span>
            <select
              className="cab-select"
              style={{ minWidth: "80px" }}
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={100}>100</option>
              <option value={200}>200</option>
              <option value={300}>300</option>
              <option value={400}>400</option>
              <option value={500}>500</option>
            </select>
          </div>

          <PermissionGuard permission="expense.create">
            <button 
              className="btn btn-primary"
              onClick={() => navigate('/ledger/expenses/add')}
              style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: '13px' }}
            >
              <FaPlus /> Add Expenses
            </button>
          </PermissionGuard>
        </div>
      </CompactActionBar>

      {/* Filters Panel */}
      {showFilters && (
        <div style={{
          background: '#fff',
          padding: '20px',
          marginBottom: '20px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleApplyFilters(); } }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            marginBottom: '16px'
          }}>
            {/* Expense Head Filter */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Expense Head
              </label>
              <select
                value={filterExpenseHead}
                onChange={(e) => setFilterExpenseHead(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              >
                <option value="">All Heads</option>
                {expenseHeads.map(head => (
                  <option key={head.id} value={head.id}>{head.name}</option>
                ))}
              </select>
            </div>

            {/* Narration Search */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Search Narration
              </label>
              <input
                type="text"
                value={filterNarration}
                onChange={(e) => setFilterNarration(e.target.value)}
                placeholder="Search in narration..."
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Expense Date From */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Expense Date From
              </label>
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>

            {/* Expense Date To */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Expense Date To
              </label>
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none'
                }}
              />
            </div>
          </div>

          {/* Filter Actions */}
          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
          }}>
            <button
              onClick={clearAllFilters}
              style={{
                padding: '8px 16px',
                background: '#fff',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                cursor: 'pointer'
              }}
            >
              Clear All
            </button>
            <button
              onClick={handleApplyFilters}
              style={{
                padding: '8px 16px',
                background: '#4f46e5',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#fff',
                cursor: 'pointer'
              }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ background: '#fff', padding: '60px', textAlign: 'center' }}>
          <div style={{fontSize: '16px', color: '#6b7280'}}>Loading expenses...</div>
        </div>
      )}

      {/* Table */}
      {!loading && (
        <div style={{ background: '#fff', margin: 0, padding: 0 }}>
          <table className="standard-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th>Sr. No</th>
                <th>Project</th>
                <th>Expense Head</th>
                <th>Date</th>
                <th>Voucher No</th>
                <th>Narration</th>
                <th style={{textAlign: 'right'}}>Amount</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {expenses.map((expense, index) => (
                <tr key={expense.id}>
                  <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                    {expenses.length - index}
                  </td>
                  <td>
                    <span style={{fontWeight: '600', color: '#1f2937'}}>{expense.project.name}</span>
                  </td>
                  <td>
                    <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                      <span style={{fontWeight: '600', color: '#1f2937'}}>{expense.expenseHead.name}</span>
                      {expense.expenseHead.description && (
                        <span style={{fontSize: '12px', color: '#6b7280'}}>{expense.expenseHead.description}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span style={{color: '#374151', fontSize: '14px'}}>{expenseService.formatDate(expense.expenseDate)}</span>
                  </td>
                  <td>
                    <span style={{fontFamily: 'monospace', color: '#1f2937', fontWeight: '500'}}>{expense.voucherNo || '-'}</span>
                  </td>
                  <td>
                    <span style={{color: '#6b7280', fontSize: '14px'}}>{expense.narration || '-'}</span>
                  </td>
                  <td style={{textAlign: 'right'}}>
                    <span style={{fontWeight: '600', color: expense.amount < 0 ? '#dc2626' : '#dc2626', fontSize: '16px'}}>{expenseService.formatAmount(expense.amount)}</span>
                  </td>
                  <td>
                    <div style={{display: 'flex', gap: '8px'}}>
                      <PermissionGuard permission="expense.view">
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => {/* View details */}}
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                      </PermissionGuard>
                      <PermissionGuard permission="expense.edit">
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleEdit(expense)}
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                      </PermissionGuard>
                      <PermissionGuard permission="expense.delete">
                        <button 
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDelete(expense)}
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </PermissionGuard>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Empty State */}
          {expenses.length === 0 && !loading && (
            <div style={{textAlign: 'center', padding: '60px', color: '#6b7280'}}>
              <FaFileInvoiceDollar style={{fontSize: '48px', marginBottom: '16px', opacity: '0.5'}} />
              <h3 style={{margin: '0 0 8px 0', color: '#374151'}}>No Expenses Found</h3>
              <p style={{margin: '0 0 24px 0'}}>Start by adding your first expense entry</p>
              <PermissionGuard permission="expense.create">
                <button 
                  className="btn btn-primary"
                  onClick={() => navigate('/ledger/expenses/add')}
                >
                  <FaPlus /> Add Expenses
                </button>
              </PermissionGuard>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {!loading && total > 0 && (
        <div className="content-section">
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <div style={{color: '#6b7280', fontSize: '14px'}}>
                Showing {((currentPage - 1) * limit) + 1} to {Math.min(currentPage * limit, total)} of {total} expenses
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <span style={{fontSize: '14px', color: '#6b7280'}}>Show:</span>
                <select 
                  value={limit} 
                  onChange={(e) => {
                    setLimit(parseInt(e.target.value));
                    setCurrentPage(1); // Reset to first page when changing limit
                  }}
                  style={{
                    padding: '4px 8px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                </select>
                <span style={{fontSize: '14px', color: '#6b7280'}}>per page</span>
              </div>
            </div>
            {totalPages > 1 && (
            <div style={{display: 'flex', gap: '8px', alignItems: 'center'}}>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let page: number;
                if (totalPages <= 5) {
                  page = i + 1;
                } else if (currentPage <= 3) {
                  page = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i;
                } else {
                  page = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={page}
                    className={`btn btn-sm ${page === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={() => setCurrentPage(page)}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        title="Delete Expense"
        message={`Are you sure you want to delete this expense? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteDialog(false)}
        type="danger"
      />
    </div>
  );
};

export default ExpensesPage;
